import {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";

export const TextareaField = forwardRef(
  (
    {
      label,
      id,
      value,
      onChange,
      placeholder,
      maxLength,
      suggestedLength,
      required = false,
      showError = true,
      allowOverLimit = false,
    },
    ref
  ) => {
    const [error, setError] = useState("");
    const [internalValue, setInternalValue] = useState(value || "");
    const [hasInteracted, setHasInteracted] = useState(false);

    const validateInput = useCallback(
      (inputValue) => {
        if (!inputValue?.trim() && required) {
          return "This field is required";
        }

        if (!inputValue) {
          return "";
        }

        if (maxLength && inputValue.length > maxLength && !allowOverLimit) {
          return `Maximum ${maxLength} characters allowed`;
        }

        return "";
      },
      [required, maxLength, allowOverLimit]
    );

    // ✅ Expose methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        validate: () => {
          const validationError = validateInput(internalValue);
          setError(validationError);
          return validationError;
        },
        touch: () => {
          setHasInteracted(true);
        },
        getValue: () => internalValue,
        getError: () => error,
        isRequired: () => required,
        reset: () => {
          setInternalValue("");
          setError("");
          setHasInteracted(false);
        },
      }),
      [validateInput, internalValue, error, required]
    );

    const handleChange = (e) => {
      const newValue = e.target.value;
      const validationError = validateInput(newValue);

      if (!hasInteracted) {
        setHasInteracted(true);
      }

      setInternalValue(newValue);
      setError(validationError);

      if (onChange && typeof onChange === "function") {
        onChange(e);
      }
    };

    const handleBlur = () => {
      if (!hasInteracted) {
        setHasInteracted(true);
      }
      const validationError = validateInput(internalValue);
      setError(validationError);
    };

    useEffect(() => {
      setInternalValue(value || "");
    }, [value]);

    useEffect(() => {
      if (hasInteracted) {
        const validationError = validateInput(internalValue);
        setError(validationError);
      }
    }, [internalValue, validateInput, hasInteracted]);

    const charCount = internalValue ? internalValue.length : 0;
    const displayLimit = suggestedLength || maxLength;
    const isOverSuggestedLimit = suggestedLength && charCount > suggestedLength;
    const isOverMaxLimit = maxLength && charCount > maxLength;

    return (
      <div className="mb-3">
        <label htmlFor={id} className="form-label">
          {label} {required ? <span className="required-field">*</span> : ""}
        </label>
        <textarea
          className={`form-control ${required ? "required" : ""} ${
            (error && hasInteracted) || (isOverMaxLimit && allowOverLimit)
              ? "is-invalid"
              : ""
          }`}
          id={id}
          placeholder={placeholder}
          value={internalValue}
          onChange={handleChange}
          onBlur={handleBlur}
          maxLength={allowOverLimit ? undefined : maxLength}
        />

        {displayLimit && (
          <div
            className={`char-counter mt-2 ${
              isOverSuggestedLimit
                ? "text-warning"
                : isOverMaxLimit
                ? "text-danger"
                : "text-muted"
            }`}
          >
            {charCount}/{displayLimit} characters
            {isOverSuggestedLimit && !isOverMaxLimit && (
              <span className="text-warning ms-2">(over suggested length)</span>
            )}
            {isOverMaxLimit && allowOverLimit && (
              <span className="text-danger ms-2">
                ({charCount - maxLength} over maximum)
              </span>
            )}
          </div>
        )}

        {showError && error && hasInteracted && (
          <div className="invalid-feedback">{error}</div>
        )}
      </div>
    );
  }
);

TextareaField.displayName = "TextareaField";
